import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IdentityButton,
  IdentityError as IdentityErrorView,
  IdentityFooterInline,
  IdentityForm,
  IdentityHero,
  IdentityLayout,
  IdentitySSOStatus,
  IdentityA11yAnnouncer,
  IdentityFormData,
  IdentityVerificationBanner,
  CANONICAL_SESSION_MESSAGES,
} from "@speedycore/identity-ui";
import {
  identityLogin,
  identityLogout,
  identitySessionHandoff,
  oauthClientIdForRedirect,
  redirectAfterAuth,
  sendIdentityTelemetry,
} from "../lib/identityApi";

const LOGIN_BULLETS = [
  "Secure login powered by SpeedyCore Identity",
  "Cross-hub access with a single account",
  "Fast, safe, RS256-backed authentication",
] as const;

export function LoginPage() {
  const [params] = useSearchParams();
  const redirectUri = useMemo(() => params.get("redirect_uri"), [params]);
  const oauthClientId = useMemo(() => oauthClientIdForRedirect(redirectUri), [redirectUri]);
  const justLoggedOut = useMemo(() => params.get("logged_out") === "1", [params]);
  const expired = useMemo(() => params.get("expired") === "1", [params]);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [a11y, setA11y] = useState<string>("");

  useEffect(() => {
    if (!justLoggedOut) return;
    void identityLogout();
  }, [justLoggedOut]);

  useEffect(() => {
    if (!redirectUri || justLoggedOut || expired) return;
    let cancelled = false;
    setHandoffBusy(true);
    void identitySessionHandoff(oauthClientId)
      .then((tokens) => {
        if (cancelled || !tokens) {
          if (!cancelled) setHandoffBusy(false);
          return;
        }
        redirectAfterAuth(redirectUri, tokens);
      })
      .catch(() => {
        if (!cancelled) setHandoffBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [redirectUri, justLoggedOut, expired, oauthClientId]);

  async function onSubmit(data: IdentityFormData) {
    setFormBusy(true);
    setFormError(null);
    try {
      const result = await identityLogin(data.email, data.password, oauthClientId);
      redirectAfterAuth(redirectUri, result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign in failed.";
      const errorWithCode = err as Error & { code?: string };
      if (errorWithCode.code === "LEGAL_ACCEPTANCE_REQUIRED") {
        const target = redirectUri
          ? `/auth/acceptance?redirect_uri=${encodeURIComponent(redirectUri)}`
          : "/auth/acceptance";
        window.location.assign(target);
        return;
      }
      setFormError(msg);
      setA11y(msg);
      setFormBusy(false);
      void sendIdentityTelemetry("identity.login_failed", { error_code: (err as Error & { code?: string }).code || "login_failed" });
    }
  }

  const signupHref = redirectUri
    ? `/auth/signup?redirect_uri=${encodeURIComponent(redirectUri)}`
    : "/auth/signup";
  const busy = handoffBusy || formBusy;

  if (handoffBusy && !formBusy && redirectUri) {
    return (
      <IdentityLayout
        hero={
          <IdentityHero
            title="Sign in to SpeedyCore"
            subtitle="One identity for MyTradieWeb, CreatorHub, HubCircles, AdvertHub, and ConstructiveOS."
            bullets={LOGIN_BULLETS}
          />
        }
        form={<IdentitySSOStatus stage="start" />}
        footer={<IdentityFooterInline />}
      />
    );
  }

  return (
    <>
      <IdentityA11yAnnouncer message={a11y} type="error" />
      <IdentityLayout
        hero={
          <IdentityHero
            title="Sign in to SpeedyCore"
            subtitle="One identity for MyTradieWeb, CreatorHub, HubCircles, AdvertHub, and ConstructiveOS."
            bullets={LOGIN_BULLETS}
          />
        }
        form={
          <>
            {expired ? (
              <div style={{ marginBottom: "1rem" }}>
                <IdentityVerificationBanner
                  tone="warning"
                  title="Session expired"
                  message={CANONICAL_SESSION_MESSAGES.expired}
                />
              </div>
            ) : null}
            <IdentityForm
              mode="login"
              onSubmit={onSubmit}
              loading={busy}
              errors={formError ? [{ message: formError }] : undefined}
              submitLabel="Continue with SpeedyCore"
            />
            {formError ? null : (
              <div style={{ display: "none" }} aria-hidden>
                <IdentityErrorView message="" />
              </div>
            )}
            <div className="sc-identity-links">
              <Link to="/auth/forgot-password">Forgot password?</Link>
              <Link to={signupHref}>Create account</Link>
            </div>
            <IdentityFooterInline />
          </>
        }
      />
    </>
  );
}
